import type { BackendObjectType } from './objectTypes';
import type { BlockType, BlockConfig } from '@/common/blocks/types';
import type { ReportConfig } from './report';

/**
 * Named contact-list definition (canonical home; re-exported by
 * modules/contacts/contactLists for intra-module consumers).
 */
export interface ContactListDef {
  id: string;
  label: string;
  color?: string;
  createdAt: string;
}

export type PrimaryModuleType =
  | 'task_list'
  | 'event_list'
  | 'contact_list'
  | 'client_list'
  | 'place_list'
  | 'places'
  | 'drive'
  | 'health'
  | 'email'
  | 'agents'
  | 'notes';

export type SupplementalModuleType =
  // Single-type alternative views
  | 'kanban' // tasks
  | 'crm' // contacts pipeline
  | 'note_grid' // notes
  | 'place_card' // place detail
  | 'file_viewer' // assets (file preview)
  // Multi-type views
  | 'activities' // unified task + crm next steps view
  | 'timeline'
  | 'calendar'
  | 'map'
  | 'news'
  | 'report'
  | 'pages'
  | 'channels'
  | 'synthesis';

export type UtilityModuleType = 'conversation_summary' | 'advice';

export type CanonicalModuleType = PrimaryModuleType | SupplementalModuleType | UtilityModuleType;

export type LegacyModuleType =
  | 'people_list'
  | 'stat'
  | 'place_detail'
  | 'updates'
  | 'message_board'
  // Keep journal/log aliases so old DB rows resolve via MODULE_ALIASES
  | 'journal'
  | 'journal_list'
  | 'log'
  | 'log-list'
  | 'log_list';

// Internal-only union while we migrate
export type ModuleType = CanonicalModuleType | LegacyModuleType;

export type ModuleSize = 'normal' | 'wide' | 'full';

export interface Module {
  id: string;
  conversation_id?: string;
  type: ModuleType;
  label: string;
  position: number; // For ordering modules within a conversation
  size?: ModuleSize; // 'normal' (1 col), 'wide' (2 cols), 'full' (3 cols)
  config?: ModuleConfig;
  parentId?: string; // For nested folders (parent module ID)
  archived?: boolean; // If true, module is hidden from normal view
  color?: string; // Muted background color for the module
  accentColor?: string; // Accent color for module-specific accent rail

  // Primary modules: exactly one objectType
  objectType?: BackendObjectType;

  // Supplemental / multi-type modules
  objectTypes?: BackendObjectType[];
}

// Module-specific configuration options
export type ModuleConfig =
  | TaskListConfig
  | DocGridConfig
  | NotesConfig
  | KanbanConfig
  | CRMConfig
  | TimelineConfig
  | PeopleListConfig
  | PlaceListConfig
  | MapConfig
  | CalendarConfig
  | GalleryConfig
  | StatConfig
  | AdviceConfig
  | CanvasModuleConfig
  | ReportConfig;

export interface TaskListConfig {
  show_completed: boolean;
  sort_by: 'due_date' | 'created_at' | 'priority';
  filter_by_status?: string[];
  viewMode?: 'list' | 'kanban';
  // Kanban-specific config (used when viewMode === 'kanban')
  columns?: KanbanColumnConfig[];
  labels?: Array<{ name: string; color: string }>; // Label library for this module
}

export interface KanbanColumnConfig {
  id: string;
  label: string;
  status: string;
  order: number;
  color?: string;
}

export interface DocGridConfig {
  columns: number; // Grid columns
  sort_by: 'created_at' | 'updated_at';
}

// NECTA note subtypes
export type NoteSubtype = 'note' | 'place' | 'stat';

export interface NotesConfig {
  sort_by: 'created_at' | 'updated_at' | 'title';
  // NECTA: Optional filter to show only specific note subtypes
  // If not set or empty, shows all subtypes
  noteTypes?: NoteSubtype[];
}

export interface KanbanConfig {
  columns: string[]; // Status columns (e.g., ["todo", "in_progress", "done"])
  show_completed: boolean;
  labels?: Array<{ name: string; color: string }>; // Workspace-level label library for this module
}

export interface CRMConfig {
  columns: string[]; // Pipeline stages (e.g., ["lead", "prospect", "negotiation", "won", "lost"])
  show_lost: boolean;
}

export interface TimelineConfig {
  view: 'day' | 'week' | 'month';
  show_past_events: boolean;
  includedTypes?: ('event' | 'task' | 'note')[];
}

export interface PeopleListConfig {
  sort_by: 'name' | 'company' | 'created_at';
  viewMode?: 'cards' | 'pipeline' | 'messages' | 'discover';
  // Pipeline-specific config (used when viewMode === 'pipeline')
  columns?: CRMColumnConfig[];
  // Card display options
  showSummary?: boolean;      // Show summary/bio on card (default: false)
  showDealValue?: boolean;    // Show deal value $ amount (default: false)
  showProbability?: boolean;  // Show close probability % (default: false)
  showNextStep?: boolean;     // Show next action due date (default: false)
  showLastContacted?: boolean; // Show last contacted date derived from notes (default: false)
  // Email template for CRM outreach
  emailTemplate?: { subject: string; body: string };
  // Reusable email response snippets
  emailResponses?: EmailResponse[];
  // Named contact lists (Phase 1). Membership is stored per-contact as `lists: string[]`.
  lists?: ContactListDef[];
}

export interface EmailResponse {
  id: string;
  name: string;       // Short label, e.g. "Fund closing date"
  content: string;    // The snippet text
  stages?: string[];  // If set, only show for contacts in these stages
}

export interface CRMColumnConfig {
  id: string;
  label: string;
  stage: string;
  order: number;
  color?: string;
}

export interface PlaceListConfig {
  filter_by_type?: string[];
  sort_by: 'name' | 'rating' | 'created_at';
  columns?: number;
  show_notes?: boolean;
}

export interface MapConfig {
  center?: { lat: number; lng: number };
  zoom: number;
  filter_by_type?: string[];
}

export interface CalendarConfig {
  view: 'month' | 'week' | 'day';
  show_past_events: boolean;
}

export interface GalleryConfig {
  columns: number;
  show_slideshow: boolean;
  visibility?: 'private' | 'public';
}

export interface StatConfig {
  stat_label: string; // Which stat to show (e.g., "weight")
  chart_type: 'line' | 'bar' | 'area';
  time_range: '7d' | '30d' | '90d' | '1y' | 'all';
  viewMode?: 'list' | 'graph' | 'dashboard'; // Which view to show by default
}

export interface AdviceConfig {
  // Chat/advice module configuration
  show_suggestions: boolean;
}

export interface CanvasBlock {
  id: string;
  type: BlockType;
  title?: string;
  span?: number;
  order?: number;
  data: Record<string, any>;
  config?: BlockConfig;
}

export interface CanvasModuleConfig {
  columns?: number;
  gap?: number;
  blocks: CanvasBlock[];
}

export type ModuleCategory = 'primary' | 'alternative' | 'multi' | 'utility';

export interface ModuleMeta {
  type: CanonicalModuleType;
  category: ModuleCategory;
  primaryObjectType?: BackendObjectType;
  objectTypes?: BackendObjectType[];
  defaultLabel: string;
  description: string;
  about?: string; // Showcases what's special about the module and how AI uses it
  aboutColor?: string; // Accent color for about dialog icon background
  icon: string; // e.g. '✓', '📅'
  advanced?: boolean; // hides behind "Advanced" toggle
  agentOnly?: boolean; // does NOT appear in add-module menus
}

export const MODULE_CATALOG: ModuleMeta[] = [
  // Primary
  {
    type: 'task_list',
    category: 'primary',
    primaryObjectType: 'task',
    defaultLabel: 'Kanban',
    description: 'To-dos and deadlines',
    about: 'AI creates, prioritizes, and tracks tasks from your conversations. Ask it to break projects into steps or reschedule your week.',
    aboutColor: '#6366f1',
    icon: '✓',
  },
  {
    type: 'event_list',
    category: 'primary',
    primaryObjectType: 'event',
    defaultLabel: 'Calendar',
    description: 'Activities and appointments',
    about: 'AI can move appointments, find open spots in your day for meetings, and manage your schedule from chat.',
    aboutColor: '#0ea5e9',
    icon: '📅',
  },
  {
    type: 'contact_list',
    category: 'primary',
    primaryObjectType: 'contact',
    defaultLabel: 'Contacts',
    description: 'People and organizations',
    about: 'Full AI-powered CRM. Look up people with simple info like a name and keyword. Enrichment finds LinkedIn, emails, and phone numbers. Email people directly from a chat-like interface.',
    aboutColor: '#ec4899',
    icon: '👤',
  },
  {
    type: 'client_list',
    category: 'primary',
    primaryObjectType: 'contact',
    defaultLabel: 'Clients',
    description: 'A roster of the people you serve',
    about: 'Track the people you work with — a searchable roster with a per-client session log and a sessions / hours / billed summary. Built for trainers, coaches, and any practice that serves clients one-on-one.',
    aboutColor: '#7C3AED',
    icon: '🤝',
  },
  {
    type: 'places',
    category: 'primary',
    primaryObjectType: 'place',
    defaultLabel: 'Places',
    description: 'Places organized in folders',
    about: 'Store all your favorite hotels, restaurants, museums, and attractions. Great for sharing, report generation, or suggesting new travel tips.',
    aboutColor: '#22c55e',
    icon: '📍',
  },
  {
    type: 'place_list',
    category: 'primary',
    primaryObjectType: 'place',
    defaultLabel: 'Places',
    description: 'Locations and venues',
    icon: '📍',
  },
  {
    type: 'drive',
    category: 'primary',
    primaryObjectType: 'asset',
    defaultLabel: 'Drive',
    description: 'Files, photos, documents',
    about: 'Everything is backed up to the cloud. Bring in the Drive files you choose — Xavior can build reports and galleries from the files you add.',
    aboutColor: '#8b5cf6',
    icon: '📁',
  },
  {
    type: 'health',
    category: 'primary',
    primaryObjectType: 'stat',
    defaultLabel: 'Health',
    description: 'Food, exercise & vitals tracking',
    about: 'Log meals and analyze nutrition. Sync your wearables — Fitbit, Whoop, Strava — and track health trends over time.',
    aboutColor: '#ef4444',
    icon: '❤️',
  },
  {
    type: 'email',
    category: 'primary',
    defaultLabel: 'Email',
    description: 'Email integration',
    about: 'AI reads your emails, drafts responses, and sends messages directly from chat.',
    aboutColor: '#0ea5e9',
    icon: '✉️',
  },
  {
    type: 'agents',
    category: 'primary',
    primaryObjectType: 'note',
    defaultLabel: 'Agents',
    description: 'Launch AI agents to do tasks',
    about: 'Describe a task and an agent researches and delivers it, then notifies you.',
    aboutColor: '#8b5cf6',
    icon: '🤖',
  },
  {
    type: 'notes',
    category: 'primary',
    primaryObjectType: 'note',
    defaultLabel: 'Notes',
    description: 'Plain-text notes and snippets',
    about: 'A flat, fast notebook for plain-text notes. Jot things down from chat ("note that…"), search them semantically, and mirror each note to Drive as a .txt. No folders, no formatting — just text.',
    aboutColor: '#0ea5e9',
    icon: '📝',
  },

  // Alternative single-type
  {
    type: 'kanban',
    category: 'alternative',
    primaryObjectType: 'task',
    objectTypes: ['task'],
    defaultLabel: 'Kanban',
    description: 'Stages for tasks & contacts',
    icon: '🧱',
  },
  {
    type: 'crm',
    category: 'alternative',
    primaryObjectType: 'contact',
    objectTypes: ['contact'],
    defaultLabel: 'CRM',
    description: 'Sales pipeline for leads and contacts',
    icon: '💼',
  },
  {
    type: 'note_grid',
    category: 'alternative',
    primaryObjectType: 'note',
    objectTypes: ['note'],
    defaultLabel: 'Doc Grid',
    description: 'Pinterest-style grid layout for docs.',
    icon: '🗂️',
  },
  {
    type: 'place_card',
    category: 'alternative',
    primaryObjectType: 'place',
    objectTypes: ['place'],
    defaultLabel: 'Place Details',
    description: 'Rich place details with photos and reviews.',
    icon: '🗺️',
  },
  {
    type: 'file_viewer',
    category: 'alternative',
    primaryObjectType: 'asset',
    objectTypes: ['asset'],
    defaultLabel: 'File Viewer',
    description: 'Preview PDFs, images, and documents.',
    icon: '📄',
  },
  // Multi-type
  {
    type: 'activities',
    category: 'multi',
    objectTypes: ['task', 'contact'],
    defaultLabel: 'Activities',
    description: 'All tasks & CRM next steps in one list',
    icon: '📋',
  },
  {
    type: 'timeline',
    category: 'multi',
    objectTypes: ['event', 'task', 'note'],
    defaultLabel: 'Timeline',
    description: 'Chronological view of conversation activity',
    icon: '⏱️',
  },
  {
    type: 'calendar',
    category: 'multi',
    objectTypes: ['event', 'task'],
    defaultLabel: 'Calendar',
    description: 'Events & tasks by date',
    icon: '🗓️',
  },
  {
    type: 'map',
    category: 'multi',
    objectTypes: ['place', 'event'],
    defaultLabel: 'Map',
    description: 'Map view of places and events.',
    icon: '🗺️',
  },
  {
    type: 'pages',
    category: 'multi',
    objectTypes: ['note'],
    defaultLabel: 'Pages',
    description: 'Reports and published pages',
    about: 'AI generates polished reports, web pages, and documents. From research summaries to business reports — created in seconds from chat.',
    aboutColor: '#3b82f6',
    icon: '📄',
  },

  // News
  {
    type: 'news',
    category: 'multi',
    defaultLabel: 'News',
    description: 'News feed and market data',
    about: 'AI curates news and market data based on your interests. Track stocks, crypto, and headlines — all in one feed.',
    aboutColor: '#6366f1',
    icon: '📰',
  },

  // Channels — team group chat
  {
    type: 'channels',
    category: 'multi',
    defaultLabel: 'Channels',
    description: 'Team group chat',
    about: 'Chat with your team in channels. Create topic-based channels for different discussions — like Slack, but inside your workspace.',
    aboutColor: '#8b5cf6',
    icon: '💬',
  },

  // Synthesis — what Xavior knows about you
  {
    type: 'synthesis',
    category: 'multi',
    defaultLabel: 'Synthesis',
    description: 'What Xavior knows about you',
    about: 'A synthesized dossier — who you are, your relationships, what’s ahead — assembled from everything Xavior has learned.',
    aboutColor: '#7c3aed',
    icon: '🧬',
  },

  // Utility
  {
    type: 'conversation_summary',
    category: 'utility',
    defaultLabel: 'Overview',
    description: 'Overview of this conversation: stats, recent activity, and quick actions.',
    icon: '🏠',
  },
  {
    type: 'advice',
    category: 'utility',
    defaultLabel: 'AI Assistant',
    description: 'Ask questions about this conversation and get suggestions.',
    icon: '✨',
  },
  // Agent-only
  {
    type: 'report',
    category: 'utility',
    defaultLabel: 'Report',
    description: 'AI-generated editorial reports',
    icon: '📄',
    agentOnly: true,
  },
];

/**
 * MODULE_ALIASES -- maps legacy/alternate type strings to canonical types.
 *
 * All aliased types resolve to their canonical type before MODULE_COMPONENTS
 * lookup in ModuleRenderer.tsx. The dedicated renderers (NoteListModule,
 * GalleryModule) were removed in 2026-05 as confirmed dead code — DriveModule
 * handles all drive-related types internally.
 */
export const MODULE_ALIASES: Record<string, CanonicalModuleType> = {
  // 'notes' is now a real primary module type (standalone Notes module), so it
  // is intentionally NOT aliased to drive. Legacy note_list/docs rows still
  // resolve to drive below.
  note_list: 'drive',
  docs: 'drive',
  photos: 'drive',
  gallery: 'drive',
  file_list: 'drive',
  asset_list: 'drive',
  log: 'drive',
  'log-list': 'drive',
  log_list: 'drive',
  journal: 'drive',
  journal_list: 'drive',
  updates: 'drive',
  message_board: 'drive',
  // Legacy email type → canonical
  gmail: 'email',
  // Unchanged
  people_list: 'contact_list',
  stat: 'health',
  stats: 'health',
  stat_list: 'health',
  place_detail: 'place_card',
};

/**
 * Map backend object types to their canonical primary module
 */
export const OBJECT_PRIMARY_MODULE: Record<BackendObjectType, CanonicalModuleType> = {
  task: 'task_list',
  event: 'event_list',
  contact: 'contact_list',
  place: 'place_list',
  asset: 'drive',
  note: 'drive',
  doc: 'drive',
  stat: 'health',
  message: 'channels',
  page: 'pages',
};

export function getModuleMeta(type: ModuleType): ModuleMeta | undefined {
  const canonicalType = (MODULE_ALIASES[type] ?? type) as CanonicalModuleType;
  return MODULE_CATALOG.find((moduleMeta) => moduleMeta.type === canonicalType);
}

export function isPrimaryModule(type: ModuleType): boolean {
  const canonicalType = (MODULE_ALIASES[type] ?? type) as CanonicalModuleType;
  return MODULE_CATALOG.some((moduleMeta) => moduleMeta.type === canonicalType && moduleMeta.category === 'primary');
}

export function isSummaryModuleType(type: string): boolean {
  return (MODULE_ALIASES[type] ?? type) === 'conversation_summary';
}

/**
 * Legacy module types that originated as note-list / journal / daily-log
 * modules before the Drive migration.  When the "+ New" button fires inside
 * one of these modules with no explicit title, DriveModule defaults to
 * today's date (e.g. "Sat May 17") and sets status='active' so entries
 * match the format the previous note-list path produced.  Fixes #2673.
 */
export const DATED_NOTE_MODULE_TYPES = new Set([
  // 'notes' (the standalone Notes module) does NOT use dated daily-log titles —
  // its titles derive from the first line. Legacy log/journal types still do.
  'note_list', 'log', 'log-list', 'log_list', 'journal', 'journal_list',
]);

/** Returns true when a module type uses dated daily-log title conventions. */
export function isDailyLogModule(type: string): boolean {
  return DATED_NOTE_MODULE_TYPES.has(type);
}

/** Format a date as "Sat May 17" — matches the Daily Log title convention. */
export function formatDailyLogTitle(date = new Date()): string {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
